import { useState, useCallback, useEffect } from 'react';

export interface RecentlyViewedItem {
  id: number;
  name: string;
  price: number;
  oldPrice?: number;
  image: string;
  color: string;
  category?: string;
  rating?: number;
  discount?: number;
  viewedAt: number;
}

const STORAGE_KEY = 'neonpharma_recently_viewed';
const MAX_ITEMS = 20;

const readStorage = (): RecentlyViewedItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as RecentlyViewedItem[]) : [];
  } catch {
    return [];
  }
};

const writeStorage = (items: RecentlyViewedItem[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
};

export const useRecentlyViewed = () => {
  const [items, setItems] = useState<RecentlyViewedItem[]>(() => readStorage());

  // Sync from storage on focus (other tabs)
  useEffect(() => {
    const onFocus = () => setItems(readStorage());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const addView = useCallback((item: Omit<RecentlyViewedItem, 'viewedAt'>) => {
    setItems(prev => {
      const filtered = prev.filter(i => i.id !== item.id);
      const updated = [{ ...item, viewedAt: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
      writeStorage(updated);
      return updated;
    });
  }, []);

  const removeItem = useCallback((id: number) => {
    setItems(prev => {
      const updated = prev.filter(i => i.id !== id);
      writeStorage(updated);
      return updated;
    });
  }, []);

  const clearAll = useCallback(() => {
    writeStorage([]);
    setItems([]);
  }, []);

  return { items, addView, removeItem, clearAll };
};
